import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useSearchParams } from "react-router";
import { Avatar, Button, Field, FormRow, FormSection, Menu, Switch, WeekHours, weekNowMark } from "@omnabase/ui";
import { DAY_KEYS, weeklyHoursLabel, type DayKey, type WorkingHours } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useCan } from "../../lib/access";
import { useParamNow, useSetParam } from "../../lib/nav";
import { useFormState, useSaver } from "../../lib/save";
import { DEFAULT_CHOICE, firstWorkingDay, hoursEditChoices, hoursPeople, localTimeIn, teamLanes, type HoursChoice, type HoursPerson } from "../../lib/teamHours";
import { useSession } from "../../shell/store";
import { HoursLines } from "./HoursLines";
import { TeamHead } from "./TeamHead";

// Settings › Team › Working hours (the working hours round, 2026-11-01; brief docs/briefs/working-hours-round.md rule 2): the admins' "Who sets
// what", one "Working hours" card and the team's week with everyone's lanes in the viewer's zone and the people list. Who sees which lane,
// number and Edit is shared's hoursAccess. The hours editor round (2026-11-02; brief docs/briefs/discussion-polish-hours-editor-round.md rules
// 8 to 11): the card's "Whose hours" picker chooses the workspace default or a person the viewer may edit (`?edit=default|<member>`); Edit in
// the people list sets it, scrolls up to the card and dresses the calendar in the person's colour; the person's row is marked meanwhile.
export function WorkingHoursTab() {
  const { workspace } = useSession();
  const team = useCan()("team");
  const asked = useParamNow("edit"); // drawn on the click itself: the picker, the colour and the row change together
  const setParam = useSetParam();
  const cardRef = useRef<HTMLDivElement>(null);
  const people = hoursPeople(workspace, team);
  const editing = people.find((p) => p.member.id === asked && p.access.edit) ?? null;
  const edit = (memberId: string) => { setParam("edit", memberId, true); scrollToCard(cardRef.current); };
  return (
    <section className="ob-profile" aria-label="Working hours">
      <TeamHead active="hours" />
      <div className="ob-settings__scroll"><div className="ob-settings__cards">
        {team && <WhoSetsWhat />}
        <HoursEditorCard people={people} editing={editing} canEditDefault={team} cardRef={cardRef} />
        <TeamWeek people={people} picked={editing?.member.id ?? null} onEdit={edit} />
      </div></div>
    </section>
  );
}

/** the page scrolls up to the Working hours card (smooth, instant under reduced motion) */
function scrollToCard(inside: HTMLElement | null) {
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  inside?.closest(".ob-form__section")?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
}

/** the admins' two switches; "Admins can always change anyone's hours." */
function WhoSetsWhat() {
  const { workspace, reload, toast } = useSession();
  const [s, setS] = useState({ hoursVisible: workspace.settings.hoursVisible, hoursSelfSet: workspace.settings.hoursSelfSet });
  useEffect(() => setS({ hoursVisible: workspace.settings.hoursVisible, hoursSelfSet: workspace.settings.hoursSelfSet }), [workspace.settings.hoursVisible, workspace.settings.hoursSelfSet]);
  const { save } = useSaver<Partial<typeof s>>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/settings`, patch); await reload(); }, toast, 0);
  const flip = (k: keyof typeof s, v: boolean) => { setS((cur) => ({ ...cur, [k]: v })); save({ [k]: v }); };
  return (
    <FormSection title="Who sets what" audience="admins" intro="Admins can always change anyone's hours.">
      <FormRow label="Members see each other's hours" help="Off, a member sees only their own lane and row."><Switch checked={s.hoursVisible} onChange={(v) => flip("hoursVisible", v)} label="Members see each other's hours" /></FormRow>
      <FormRow label="Members set their own hours" help="Off, only admins change a member's hours."><Switch checked={s.hoursSelfSet} onChange={(v) => flip("hoursSelfSet", v)} label="Members set their own hours" /></FormRow>
    </FormSection>
  );
}

const DEFAULT_INTRO = "For everyone without hours of their own, read in each person's own time zone. Notifications outside them are held until the next working hour.";

/** the one "Working hours" card: the workspace default (editable with the team right, read-only for everyone else) or one person's hours,
 *  chosen in the picker at its top right */
function HoursEditorCard({ people, editing, canEditDefault, cardRef }: { people: HoursPerson[]; editing: HoursPerson | null; canEditDefault: boolean; cardRef: RefObject<HTMLDivElement | null> }) {
  const choices = hoursEditChoices(people);
  const picker = choices.length > 1 ? <WhosePicker choices={choices} picked={editing?.member.id ?? DEFAULT_CHOICE.id} /> : null;
  return editing ? <PersonHours key={editing.member.id} person={editing} picker={picker} cardRef={cardRef} /> : <DefaultHours editable={canEditDefault} picker={picker} cardRef={cardRef} />;
}

/** "Whose hours": the workspace default and the people this viewer may edit, each with their dot in their colour (`?edit=`) */
function WhosePicker({ choices, picked }: { choices: HoursChoice[]; picked: string }) {
  const setParam = useSetParam();
  const current = choices.find((c) => c.id === picked) ?? DEFAULT_CHOICE;
  const dot = (tint: string | null) => tint ? <span className="ob-weekhours-dot" style={{ "--av": tint } as CSSProperties} aria-hidden="true" /> : null;
  return (
    <Menu align="end" trigger={<Field label="Whose hours" value={current.label} avatar={dot(current.tint)} className="ob-hours__whose" />}
      sections={[{ label: "Whose hours", items: choices.map((c) => ({ id: c.id, label: c.label, dot: c.tint ?? undefined, checked: c.id === current.id, onSelect: () => setParam("edit", c.id === DEFAULT_CHOICE.id ? null : c.id, true) })) }]} />
  );
}

/** the workspace's default hours */
function DefaultHours({ editable, picker, cardRef }: { editable: boolean; picker: ReactNode; cardRef: RefObject<HTMLDivElement | null> }) {
  const { workspace, reload, toast } = useSession();
  const { save } = useSaver<{ hours: WorkingHours }>(async (patch) => { await api.patch(`/api/v1/w/${workspace.slug}/settings`, patch); await reload(); }, toast);
  return <HoursCard intro={DEFAULT_INTRO} stateKey="default" hours={workspace.settings.hours} onChange={editable ? (hours) => save({ hours }) : undefined} action={picker} cardRef={cardRef} />;
}

/** one person's hours, read in their own zone, in their colour, with "Use the workspace default" while they have their own */
function PersonHours({ person: p, picker, cardRef }: { person: HoursPerson; picker: ReactNode; cardRef: RefObject<HTMLDivElement | null> }) {
  const { workspace, reload, toast } = useSession();
  const url = p.you ? `/api/v1/w/${workspace.slug}/me/settings` : `/api/v1/w/${workspace.slug}/members/${p.member.id}`;
  const { save } = useSaver<{ hours: WorkingHours | null }>(async (patch) => { await api.patch(url, patch); await reload(); }, toast);
  const useDefault = async () => { try { await api.patch(url, { hours: null }); await reload(); } catch (e) { toast((e as Error).message, { kind: "error" }); } };
  return (
    <HoursCard stateKey={`person:${p.member.id}:${p.own ? "own" : "default"}`} hours={p.hours} onChange={(hours) => save({ hours })} tint={p.tint} cardRef={cardRef}
      intro={p.own ? "Their own hours, read in their own time zone." : "The workspace default until you change a day; read in their own time zone."}
      action={<span className="ob-hours__actions">{p.own && <Button size="sm" onClick={() => void useDefault()}>Use the workspace default</Button>}{picker}</span>} />
  );
}

/** the calendar card: the week, the picked day (`?day=`) and the lines under the grid; without `onChange` it only reads */
function HoursCard({ intro, hours: given, stateKey, onChange, action, tint, cardRef }: { intro: string; hours: WorkingHours; stateKey: string; onChange?: (hours: WorkingHours) => void; action?: ReactNode; tint?: string; cardRef: RefObject<HTMLDivElement | null> }) {
  const { workspace } = useSession();
  const [params] = useSearchParams(); const setParam = useSetParam();
  const [hours, setHours] = useFormState(given, stateKey);
  const asked = params.get("day") as DayKey | null;
  const day = asked && (DAY_KEYS as readonly string[]).includes(asked) ? asked : firstWorkingDay(hours);
  const change = (next: WorkingHours) => { setHours(next); onChange?.(next); };
  return (
    <FormSection title="Working hours" intro={intro} action={action}>
      <div ref={cardRef} className="ob-hours__editor">
        <WeekHours mode="edit" label="Working hours" hours={hours} weekStart={workspace.settings.weekStart} tint={tint} selected={onChange ? day : null}
          onSelect={onChange ? (d) => setParam("day", d) : undefined} onChange={onChange ? change : undefined} />
        {onChange ? <HoursLines day={day} hours={hours} onChange={change} /> : <p className="ob-hours__line"><span className="ob-hours__total">{weeklyHoursLabel(hours)} a week</span></p>}
      </div>
    </FormSection>
  );
}

/** the minute tick for the local times and the now line */
function useMinute(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), 60_000); return () => window.clearInterval(t); }, []);
  return now;
}

/** the team's week in the viewer's zone and the people under it */
function TeamWeek({ people, picked, onEdit }: { people: HoursPerson[]; picked: string | null; onEdit: (memberId: string) => void }) {
  const { me, workspace } = useSession();
  const now = useMinute();
  const zone = me.profile.timezone ?? workspace.settings.timezone;
  return (
    <FormSection title="The team's week" intro={`Everyone's hours in your time zone, ${zone}.`}>
      <WeekHours mode="team" label="The team's week" lanes={teamLanes(people, zone, now)} viewerId={workspace.me.memberId} now={weekNowMark(now, zone)} weekStart={workspace.settings.weekStart} />
      <PeopleList people={people} now={now} picked={picked} onEdit={onEdit} />
    </FormSection>
  );
}

/** person (colour, avatar, name, "· You"), time zone with the local time now, hours a week, Default or Own hours, Edit; the picked person's row
 *  is marked (`aria-current`) */
function PeopleList({ people, now, picked, onEdit }: { people: HoursPerson[]; now: Date; picked: string | null; onEdit: (memberId: string) => void }) {
  return (
    <div className="ob-dtable-card ob-hours__people">
      <table className="ob-dtable">
        <thead><tr><th>Person</th><th>Time zone</th><th className="ob-dtable__num ob-dtable__col--phone">Hours a week</th><th className="ob-dtable__col--phone">Hours</th><th /></tr></thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.member.id} className={p.member.id === picked ? "ob-hours__row--picked" : undefined} aria-current={p.member.id === picked ? "true" : undefined}>
              <td className="ob-dtable__grow"><span className="ob-dtable__person"><span className="ob-weekhours-dot" style={{ "--av": p.tint } as CSSProperties} aria-hidden="true" /><Avatar size="sm" initials={p.member.initials} src={p.member.imageUrl ?? undefined} /><span className="ob-dtable__who"><span className="ob-dtable__name" title={p.member.name}>{p.member.name}{p.you && <span className="ob-dtable__muted"> · You</span>}</span></span></span></td>
              <td><span className="ob-dtable__who"><span className="ob-dtable__name">{p.zone}</span><span className="ob-dtable__email">{localTimeIn(p.zone, now)} now</span></span></td>
              <td className="ob-dtable__num ob-dtable__col--phone">{p.access.total ? <> {weeklyHoursLabel(p.hours)} </> : ""}</td>{/* the spaces keep the number a word of its own in the row's text */}
              <td className="ob-dtable__col--phone">{p.own ? "Own hours" : "Default"}</td>
              <td className="ob-dtable__actions">{p.access.edit && <Button size="sm" onClick={() => onEdit(p.member.id)}>Edit</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
